import { Group, Popover, Text, UnstyledButton } from '@mantine/core';
import { IconVolume } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getRealtimeVoiceOptions,
  REALTIME_DEFAULT_VOICE_MAP,
  REALTIME_VOICE_OPTIONS,
  RealtimeProvider,
  resolveRealtimeVoice,
} from '../types/realtime';

export interface VoiceOption {
  value: string;
  label: string;
}

const SvgSelectCheck = () => (
  <svg
    width="14"
    height="10"
    viewBox="0 0 10 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="flex-shrink-0"
  >
    <path
      d="M4 4.586L1.707 2.293A1 1 0 1 0 .293 3.707l3 3a.997.997 0 0 0 1.414 0l5-5A1 1 0 1 0 8.293.293L4 4.586z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
    />
  </svg>
);

const SvgArrow = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17 10L15.2527 11.763C13.8592 13.1689 13.1625 13.8719 12.3133 13.9801C12.1053 14.0066 11.8947 14.0066 11.6867 13.9801C10.8375 13.8719 10.1408 13.1689 8.74731 11.763L7 10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

interface VoiceSelectorProps {
  value?: string;
  onChange?: (voice: string) => void;
  disabled?: boolean;
  // 数字人/透明背景样式（文字颜色继承父元素）
  isDigitalHuman?: boolean;
  // Widget 场景，文字颜色继承父元素但保留边框
  isWidget?: boolean;
  provider?: RealtimeProvider;
  model?: string;
  // 触发按钮宽度，默认 auto
  width?: number | string;
}

/**
 * Realtime 音色选择器 - 供 realtime 页面和 admin 设置页共用
 */
export const VoiceSelector = ({
  value,
  onChange,
  disabled = false,
  isDigitalHuman = false,
  isWidget = false,
  provider = 'gemini',
  model,
  width,
}: VoiceSelectorProps) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState<boolean>(false);

  const voiceList = getRealtimeVoiceOptions(provider, model);
  const currentVoice = resolveRealtimeVoice(provider, model, value);
  const selectedVoice = voiceList.find((option) => option.value === currentVoice) || voiceList[0];

  const handleSelect = (voiceValue: string) => {
    onChange?.(voiceValue);
    setOpened(false);
  };

  return (
    <Popover opened={opened && !disabled} onChange={setOpened} width={240} position="bottom" withArrow>
      <Popover.Target>
        <div
          className={`flex items-center justify-between flex-none ${
            isDigitalHuman ? '' : 'border rounded-md sm:rounded-full px-2 py-1'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          style={width !== undefined ? { width, minWidth: width } : undefined}
          onClick={() => !disabled && setOpened((o) => !o)}
        >
          <Group gap={5} wrap="nowrap" className="flex-1">
            <IconVolume
              size={12}
              className={`flex-shrink-0 ${isDigitalHuman || isWidget ? 'text-inherit' : 'text-gray-800'}`}
            />
            <Text size="xs" c={isDigitalHuman || isWidget ? 'inherit' : 'gray.8'}>
              {selectedVoice.label}
            </Text>
          </Group>
          <div
            className={`transition-transform duration-200 ${
              isDigitalHuman || isWidget ? 'text-inherit' : 'text-gray-8'
            }`}
            style={{ transform: opened ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <SvgArrow />
          </div>
        </div>
      </Popover.Target>

      <Popover.Dropdown className="p-2.5 max-h-80 overflow-y-auto">
        {voiceList.map((option) => (
          <UnstyledButton
            key={option.value}
            className="w-full"
            onClick={() => handleSelect(option.value)}
            disabled={disabled}
          >
            <Group
              gap={8}
              className="w-full hover:bg-gray-100 p-1 my-1 rounded-md"
              justify="space-between"
              wrap="nowrap"
            >
              <div className="flex-1">
                <Text size="xs" fw={500} lineClamp={1} title={option.label}>
                  {option.label}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={1} title={t(`realtime.voice_characteristics.${option.value}`)}>
                  {t(`realtime.voice_characteristics.${option.value}`)}
                </Text>
              </div>
              {option.value === currentVoice && (
                <div
                  style={{
                    // realtime/数字人/widget 场景：白底下拉，深灰勾选图标对比柔和
                    // admin 设置页：用品牌紫突出已选项
                    color:
                      isDigitalHuman || isWidget
                        ? 'var(--mantine-color-gray-5, #495057)'
                        : 'var(--mantine-color-violet-6, #7C68FB)',
                  }}
                >
                  <SvgSelectCheck />
                </div>
              )}
            </Group>
          </UnstyledButton>
        ))}
      </Popover.Dropdown>
    </Popover>
  );
};

// 向后兼容旧导出名
export const GEMINI_VOICE_OPTIONS = REALTIME_VOICE_OPTIONS.gemini;
export const OPENAI_VOICE_OPTIONS = REALTIME_VOICE_OPTIONS.openai;
export const DEFAULT_VOICE_MAP = REALTIME_DEFAULT_VOICE_MAP;
